import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { authorizationContent, baseContent, TContent } from 'constants/content'

import { interWbSession } from '@interco/inter-webview-bridge'

import { TGlobalCtx, THeaderPage } from './types'

const DEFAULT_VALUE: TGlobalCtx = {
  setHeaderPageContext: () => {
    throw new Error('Not Implemented')
  },
  loadingGlobal: false,
  setLoadingGlobal: () => {
    throw new Error('Not Implemented')
  },
  setHeaderPage: () => {
    throw new Error('Not Implemented')
  },
  appInfoContext: null,
  setInterIDContent: () => {
    throw new Error('Not Implemented')
  },
  content: baseContent,
}

const GlobalContext = createContext<TGlobalCtx>(DEFAULT_VALUE)

export const useGlobalContext = () => useContext(GlobalContext)

export const withGlobalContext = (Component: React.FC) => (props: Record<string, unknown>) => {
  const [loadingGlobal, setLoadingGlobalState] = useState<boolean>(() => {
    const stored = sessionStorage.getItem('loadingGlobal')
    return stored ? JSON.parse(stored) : false
  })

  const [headerPage, setHeaderPageState] = useState<THeaderPage | undefined>(() => {
    const stored = sessionStorage.getItem('headerPage')
    return stored && stored !== 'undefined' ? JSON.parse(stored) : undefined
  })

  const [content, setContent] = useState<TContent>(DEFAULT_VALUE.content)

  const [appInfoContext, setAppInfoContext] = useState<unknown>(null)

  const setLoadingGlobal = useCallback((loadingValue: boolean) => {
    setLoadingGlobalState(loadingValue)
    sessionStorage.setItem('loadingGlobal', JSON.stringify(loadingValue))
  }, [])

  const setHeaderPage = useCallback((headerValue: THeaderPage | undefined) => {
    setHeaderPageState(headerValue)
    sessionStorage.setItem('headerPage', JSON.stringify(headerValue))
  }, [])

  const setInterIDContent = useCallback(() => {
    const contentData = authorizationContent
    setContent(contentData)
  }, [])

  useEffect(() => {
    const getAppInfo = async () => {
      const appInfoData = await interWbSession.getAppInfo()
      setAppInfoContext(appInfoData)
    }
    getAppInfo()
  }, [])

  const value = useMemo(
    () => ({
      setHeaderPageContext: setHeaderPageState,
      loadingGlobal,
      setLoadingGlobal,
      headerPage,
      setHeaderPage,
      appInfoContext,
      content,
      setInterIDContent,
    }),
    [
      headerPage,
      loadingGlobal,
      setLoadingGlobal,
      setHeaderPage,
      appInfoContext,
      content,
      setInterIDContent,
    ],
  )

  return (
    <GlobalContext.Provider value={value}>
      <Component {...props} />
    </GlobalContext.Provider>
  )
}
